import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import EventCard from '../components/EventCard';
import { Search, SlidersHorizontal, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sample events for demo
const sampleEvents = [
  {
    id: '1',
    title: 'Gaming Stream Night',
    description: 'Join AlexGamer for an epic gaming stream with exclusive giveaways and Q&A session.',
    influencer_name: 'AlexGamer',
    category: 'gaming',
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Virtual',
    image_url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&h=300&fit=crop',
    total_tickets: 100,
    sold_tickets: 78,
    ticket_tiers: [{ name: 'General', price: 25, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '2',
    title: 'Beauty Masterclass',
    description: 'Learn professional makeup techniques in this hands-on workshop.',
    influencer_name: 'Mia Glam',
    category: 'beauty',
    event_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Los Angeles, CA',
    image_url: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400&h=300&fit=crop',
    total_tickets: 50,
    sold_tickets: 35,
    ticket_tiers: [{ name: 'VIP', price: 150, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '3',
    title: 'Live Podcast Show',
    description: 'Exclusive live recording with audience Q&A and meet & greet.',
    influencer_name: 'Jake & Tom',
    category: 'comedy',
    event_date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'New York, NY',
    image_url: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=400&h=300&fit=crop',
    total_tickets: 200,
    sold_tickets: 180,
    ticket_tiers: [{ name: 'General', price: 45, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '4',
    title: 'Concert with DJ SonicWave',
    description: 'An unforgettable night of electronic music and visual effects.',
    influencer_name: 'DJ SonicWave',
    category: 'music',
    event_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Miami, FL',
    image_url: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&h=300&fit=crop',
    total_tickets: 500,
    sold_tickets: 420,
    ticket_tiers: [{ name: 'General', price: 75, sale_mode: 'fixed' }, { name: 'VIP', price: 200, sale_mode: 'auction', min_bid: 150 }],
    status: 'published'
  },
  {
    id: '5',
    title: 'Dinner with LifestyleLexi',
    description: 'An intimate dinner experience with lifestyle tips and networking.',
    influencer_name: 'LifestyleLexi',
    category: 'lifestyle',
    event_date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Chicago, IL',
    image_url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop',
    total_tickets: 8,
    sold_tickets: 6,
    ticket_tiers: [{ name: 'Dinner Seat', price: 500, sale_mode: 'auction', min_bid: 400 }],
    status: 'published'
  },
  {
    id: '6',
    title: 'Fitness Bootcamp',
    description: 'High-intensity training session with professional fitness coach.',
    influencer_name: 'FitBro',
    category: 'fitness',
    event_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Austin, TX',
    image_url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop',
    total_tickets: 30,
    sold_tickets: 28,
    ticket_tiers: [{ name: 'Participant', price: 99, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '7',
    title: 'Tech Talk: AI & Future',
    description: 'Deep dive into AI trends with leading tech influencer.',
    influencer_name: 'TechSavvy',
    category: 'tech',
    event_date: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'San Francisco, CA',
    image_url: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&h=300&fit=crop',
    total_tickets: 150,
    sold_tickets: 89,
    ticket_tiers: [{ name: 'Standard', price: 60, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '8',
    title: 'Cooking Class with ChefMike',
    description: 'Learn to cook authentic Italian cuisine from scratch.',
    influencer_name: 'ChefMike',
    category: 'food',
    event_date: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Boston, MA',
    image_url: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=300&fit=crop',
    total_tickets: 20,
    sold_tickets: 15,
    ticket_tiers: [{ name: 'Chef Table', price: 175, sale_mode: 'fixed' }],
    status: 'published'
  }
];

const categories = [
  { id: 'all', name: 'All Categories' },
  { id: 'gaming', name: 'Gaming' },
  { id: 'beauty', name: 'Beauty & Fashion' },
  { id: 'music', name: 'Music & Concerts' },
  { id: 'comedy', name: 'Comedy' },
  { id: 'fitness', name: 'Fitness & Sports' },
  { id: 'food', name: 'Food & Cooking' },
  { id: 'tech', name: 'Tech & Business' },
  { id: 'lifestyle', name: 'Lifestyle' }
];

export default function BrowseEvents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, search, category, sortBy]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events`);
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = [...events];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(e => 
        e.title.toLowerCase().includes(searchLower) ||
        e.description?.toLowerCase().includes(searchLower) ||
        e.influencer_name.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (category && category !== 'all') {
      filtered = filtered.filter(e => e.category === category);
    }

    // Sort
    switch (sortBy) {
      case 'date':
        filtered.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
        break;
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = Math.min(...(a.ticket_tiers?.map(t => t.price) || [0]));
          const priceB = Math.min(...(b.ticket_tiers?.map(t => t.price) || [0]));
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = Math.min(...(a.ticket_tiers?.map(t => t.price) || [0]));
          const priceB = Math.min(...(b.ticket_tiers?.map(t => t.price) || [0]));
          return priceB - priceA;
        });
        break;
      case 'popular':
        filtered.sort((a, b) => b.sold_tickets - a.sold_tickets);
        break;
      default:
        break;
    }

    setFilteredEvents(filtered);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(prev => {
      if (search) prev.set('search', search);
      else prev.delete('search');
      return prev;
    });
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('all');
    setSortBy('date');
    setSearchParams({});
  };

  const hasActiveFilters = search || category !== 'all';

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2">Browse Events</h1>
          <p className="text-muted-foreground">Discover exclusive experiences with your favorite creators</p>
        </div>

        {/* Filters */}
        <div className="bg-card border border-white/10 rounded-xl p-4 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search events, creators..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-secondary/50"
                  data-testid="search-input"
                />
              </div>
            </form>

            {/* Category */}
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full lg:w-48 bg-secondary/50" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full lg:w-40 bg-secondary/50" data-testid="sort-filter">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="gap-2" data-testid="clear-filters">
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10">
              {search && (
                <Badge variant="secondary" className="gap-1">
                  Search: {search}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSearch('')} />
                </Badge>
              )}
              {category !== 'all' && (
                <Badge variant="secondary" className="gap-1 capitalize">
                  {category}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setCategory('all')} />
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-4 text-sm text-muted-foreground">
          {filteredEvents.length} events found
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-white/10" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-white/10 rounded w-3/4" />
                  <div className="h-4 bg-white/10 rounded w-1/2" />
                  <div className="h-4 bg-white/10 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-16">
            <SlidersHorizontal className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-xl mb-2">No events found</h3>
            <p className="text-muted-foreground mb-4">Try adjusting your filters or search terms</p>
            <Button onClick={clearFilters}>Clear All Filters</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredEvents.map((event, index) => (
              <div 
                key={event.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <EventCard event={event} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
